import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { StateService, Transition, TransitionService, UiOnParamsChanged } from '@uirouter/angular';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { IUserData } from '@app/shared/services/auth/auth-data.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import * as _ from 'underscore';
import { util } from '@app/shared/util';
import { ICompanyCountry, ICountry, INamedObject, IObject } from '@type_definitions/datatypes';
import { LOV_COUNTRY, LovService } from '@app/shared/services/lov.service';
import { EDITOR_TINYMCE, EditorConfigService, TINYMCE_ADVANCED } from '@app/shared/editor/editor-config.service';
import { IOrderSearchResult, ISearchResults, OrderSearchDataService } from '@app/shared/services/order/order-search-data.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { WebshopDataService } from '../webshop/webshop-data.service';
import { GaFormlyValidatorService } from '@app/shared/formly/ga-formly-validator.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { GaMessageService, IDialogButton } from '@app/shared/message/ga-message.service';
import { ScrollService } from '@app/shared/scroll/scroll.service';
import { OrderKeywordsAddonComponent } from './order-keywords-addon.component';
import { OrderSearchResultComponent } from './order-search-result.component';
import { SowActivationComponent } from './sow-activation.component';
import { FaqTicketCategoryComponent } from './faq-ticket-category.component';
import { STATE_HOME_PAGE } from '@app/shared/app-states';
import { Subscription } from 'rxjs';
import { ITicket, ITicketCategory, ITicketGuideButton, TICKET_WORKFLOW_TYPE_ORDER, TICKET_WORKFLOW_TYPE_TRAINING, TICKET_WORKFLOW_TYPE_WEBSHOP, TicketDataService } from '@app/shared/ticket/ticket-data.service';
import { ISOWInfo, SOW_TYPE_SERVICE, SOWDataService } from '@app/shared/sow/sow-data.service';
import { GaUserService } from '@app/shared/services/ga-user.service';

export interface ITicketGuideFormModel extends ITicket {
  orderKeywords?:string;
  documents?:number[];
}

export interface ITicketGuideComponentInputs {
  orderId?:number;
  webshopId?:string;
  countryId?:string;
  isInDialog?:boolean;
  workflowTypes?:string[];
  requesterId?:number;
  category?:string;
  comments?:string;
  description?:string;
  companyCountry?:ICompanyCountry;
  hideGuideButtons?:boolean;
}

@Component({
  selector: 'ticket-guide',
  template: `
    <div class="form-header" [class.modal-header]="isInDialog">
      <h4 [ngClass]="{ 'ticket-title': !isInDialog, 'modal-title': isInDialog }" translate>{{isTrainingTicket ? 'APP.TICKET.TITLE_TRAINING' : 'APP.TICKET.TITLE'}}</h4>
    </div>

    <div class="form-body" [class.modal-body]="isInDialog">
      <div class="ticket-type" *ngIf="ticketGuideButtons?.length">
        <div class="choice">
          <div class="row">
            <div class="col-sm-3 col-lg-space-bottom" *ngFor="let guide of ticketGuideButtons">
              <button class="btn choice-button" [ngClass]="{'btn-info': selectedTicketGuide?.code === guide.code}" (click)="onTicketGuideChange(guide)">
                <strong>{{'APP.TICKET.GUIDEBUTTONS.' + guide.code + '.DESCRIPTION' | translateDefault: ''}}</strong><br/>
                <span>{{'APP.TICKET.GUIDEBUTTONS.' + guide.code + '.COMMENTS' | translateDefault: ''}}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="showForm && !noTrainingTicketButtons">
        <div class="form-horizontal">
          <formly [form]="form" [fields]="fields" [model]="model"></formly>
        </div>

        <span class="clearfix"></span>
      </div>
      
      <div *ngIf="noTrainingTicketButtons" translate>APP.TICKET.GUIDEBUTTONS.NO_TRAININGS_AVAILABLE</div>
      <h3 *ngIf="loading"><activity-indicator></activity-indicator></h3>
    </div>

    <div class="form-footer ticket-footer" [class.modal-footer]="isInDialog">
      <div class="row">
        <div class="col-sm-12" [class.p-0]="isInDialog">
          <button class="btn btn-primary" id="save-ticket-btn" (click)="createTicket()" [busyButton]="saving" *ngIf="hasWritePermission && !noTrainingTicketButtons">
            <span translate>{{isTrainingTicket ? 'APP.TICKET.TITLE_TRAINING' : 'APP.TICKET.BUTTONS.CREATE_TICKET'}}</span>
          </button>
          <button class="btn choice-button" id="cancel-ticket-btn" (click)="cancelForm()" translate>APP.TICKET.BUTTONS.CANCEL</button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./ticket-guide.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TicketGuideComponent implements OnInit, OnChanges, OnDestroy, ITicketGuideComponentInputs, UiOnParamsChanged {
  @Input() public orderId: number;
  @Input() public isInDialog: boolean;
  @Input() public workflowTypes: string[];
  @Input() public requesterId: number;
  @Input() public category: string;
  @Input() public comments: string;
  @Input() public description: string;
  @Input() public companyCountry: ICompanyCountry;
  @Input() public hideGuideButtons: boolean;
  @Input() public webshopId:string;
  @Input() public countryId:string;
  @Output() public onDialogClose: EventEmitter<ITicket> = new EventEmitter<ITicket>();

  public ticketGuideButtons: ITicketGuideButton[];
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public fields: IExtraFieldConfigurationObject[];
  public model: ITicketGuideFormModel = {};
  public sowInfo: ISOWInfo;
  public hasWritePermission: boolean;
  public selectedTicketGuide: ITicketGuideButton;
  public saving: boolean;
  public showForm: boolean;
  public loading:boolean;

  private uploadedDocumentIds?:number[];
  private categoryField:IExtraFieldConfigurationObject;
  private faqField:IExtraFieldConfigurationObject;
  private searchResultTemplate:IExtraFieldConfigurationObject;
  private orderField:IExtraFieldConfigurationObject;
  private webshopField:IExtraFieldConfigurationObject;
  private countryField:IExtraFieldConfigurationObject;
  private descriptionField:IExtraFieldConfigurationObject;
  private commentsField:IExtraFieldConfigurationObject;
  private sowActivationField: IExtraFieldConfigurationObject;
  private previousState:string;
  private currentUser:IUserData;
  private checkSOWBusy: boolean;
  private isReady: boolean;
  private stopTransitionEnterEventListener: Function;
  private uploadCompletedEventListener: Subscription;

  constructor(
    private transitionService: TransitionService,
    private authService: AuthService,
    private formlyService: GaFormlyService,
    private searchDataService: OrderSearchDataService,
    private ticketDataService: TicketDataService,
    private sowDataService: SOWDataService,
    private formattingService: FormattingService,
    private webshopDataService: WebshopDataService,
    private lovService: LovService,
    private formlyValidatorService: GaFormlyValidatorService,
    private translationService: TranslationService,
    private stateService: StateService,
    private validateService: GaValidateService,
    private messageService: GaMessageService,
    private scrollService: ScrollService,
    private editorConfigService: EditorConfigService,
    private userService:GaUserService
  ) {
    /**/
  }

  public ngOnInit(): void {
    this.workflowTypes = this.workflowTypes || [TICKET_WORKFLOW_TYPE_ORDER, TICKET_WORKFLOW_TYPE_WEBSHOP];
    this.currentUser = this.authService.getCurrentUser();

    // Create Form
    this.fields = this.createFields();

    // register listener
    this.listen();

    // get ticket guide buttons
    if (!this.hideGuideButtons) {
      this.loading = true;
      this.ticketDataService.getTicketGuideButtons(this.workflowTypes?.join(','))
        .then((buttons: ITicketGuideButton[]) => {
          this.ticketGuideButtons = buttons;
          this.isReady = true;
          this.initialSelectTicketGuideButton();
        })
        .finally(() => {
          this.loading = false;
        });
    } else {
      this.isReady = true;
      this.initialSelectTicketGuideButton();
    }
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.comments?.currentValue) {
      this.model.comments = changes.comments.currentValue;
      this.form.patchValue({ comments: changes.comments.currentValue });
    }
    if (changes.description?.currentValue) {
      this.model.description = changes.description.currentValue;
      this.form.patchValue({ description: changes.description.currentValue });
    }
    if (changes.companyCountry?.currentValue) {
      this.model.companyCountry = changes.companyCountry.currentValue;
    }
  }

  public uiOnParamsChanged(newParams: IObject): void {
    this.orderId = newParams?.orderId;
    this.webshopId = newParams?.webshopId;
    this.countryId = newParams?.countryId;
    
    if (this.isReady) {
      this.onOrderIdChange();
      this.onWebshopIdChange();
      this.onCountryIdChange();
    }
  }

  private initialSelectTicketGuideButton() {
    if (this.orderId && this.ticketGuideButtons?.length) {
      this.onOrderIdChange();
    } else if (this.workflowTypes?.length === 1 && !this.ticketGuideButtons?.length) {
      this.onTicketGuideChange()
        .then(() => { //select category
          const cat = this.categoryField.templateOptions.options.find((c:ITicketCategory) => c.categoryType === this.category);
          if (cat) {
            this.categoryField.templateOptions.options = [cat];
            this.form.patchValue({ category: cat });
            this.onCategoryChange(cat);
          }
        });
    }
  }

  private onOrderIdChange(): void {
    if (this.orderId) {
      this.onTicketGuideChange(this.ticketGuideButtons[ 0 ]);
      this.model.orderKeywords = this.orderId?.toString();
      this.searchOrder(this.orderId);
    }
  }

  private onWebshopIdChange():void {
    if (this.webshopId && this.webshopField) {
      this.webshopField.formControl.setValue(_.findWhere(this.webshopField.templateOptions.options, { id: Number(this.webshopId) || '' }));
    }
  }

  private onCountryIdChange():void {
    if (this.countryId && this.countryField) {
      this.countryField.formControl.setValue(_.findWhere(this.countryField.templateOptions.options, { id: Number(this.countryId) || '' }));
    }
  }

  private listen = ():void => {
    // listen to onEnter state transition to store previous state
    this.stopTransitionEnterEventListener = this.transitionService.onEnter({}, (transition: Transition) => {
      this.previousState = transition.$from().name;
    }, { invokeLimit: 1 });

    // this collects a list of all uploaded documents and link them to the ticket at the moment of creation
    this.uploadCompletedEventListener = this.editorConfigService.onUploadCompleted.subscribe((uploadedDocumentIds) => {
      this.uploadedDocumentIds = _.uniq((this.uploadedDocumentIds || []).concat(uploadedDocumentIds));
    });
  };

  private clearModel() {
    this.model = _.isNumber(this.model) ? {} : this.model || {};
    this.model.category = undefined;
    this.form.patchValue(this.model);
    this.updateFaqs();

    if (this.faqField) {
      this.faqField.hide = true;
    }
  }

  private hideFormFieldsOfType(type:string):void {
    if (type === TICKET_WORKFLOW_TYPE_ORDER) {
      this.orderField.hide = false;
      // only hide search result when there aren't any
      this.searchResultTemplate.hide = !this.searchResultTemplate.templateOptions.inputs.orders || !this.searchResultTemplate.templateOptions.inputs.orders.length;
      this.webshopField.hide = true;
      this.countryField.hide = true;
    } else if (type === TICKET_WORKFLOW_TYPE_WEBSHOP) {
      this.orderField.hide = true;
      this.searchResultTemplate.hide = true;
      this.webshopField.hide = false;
      this.countryField.hide = false;
    } else {
      this.orderField.hide = true;
      this.searchResultTemplate.hide = true;
      this.webshopField.hide = true;
      this.countryField.hide = true;
    }
  }

  public get isTrainingTicket(): boolean {
    return _.contains(this.workflowTypes, TICKET_WORKFLOW_TYPE_TRAINING);
  }

  // for training tickets a message is being displayed when there are no workflow buttons
  public get noTrainingTicketButtons():boolean {
    return this.isTrainingTicket && this.isReady && !this.ticketGuideButtons?.length;
  }

  public onTicketGuideChange(ticketGuide?:ITicketGuideButton):Promise<any> {
    const workflowType = this.getSelectedWorkflowType(ticketGuide);
    this.showForm = !!(workflowType);
    // initiate models
    this.clearModel();

    this.form.markAsPristine();
    this.form.markAsUntouched();

    this.sowInfo = undefined;
    this.selectedTicketGuide = ticketGuide;
    this.hideFormFieldsOfType(workflowType);

    // check open orders if workflow type is PO and orderId is not set
    if (workflowType === TICKET_WORKFLOW_TYPE_ORDER && !this.orderId) {
      this.checkOpenOrder();
    }

    // When ticket guide change to type 'Service request' but orderId is selected then we should immediately trigger change
    // Then we can check if SOW service ticket is activated for this webshop and country specific to this order
    if (ticketGuide?.code === SOW_TYPE_SERVICE && this.model.orderId && this.searchResultTemplate?.templateOptions?.options?.length) {
      this.onOrderSelectionChange(_.findWhere(this.searchResultTemplate.templateOptions.inputs.orders, { id: this.model.orderId }));
    }

    this.hasWritePermission = this.getWritePermission();
    this.setTicketMessageHidden();

    // filling all options fields
    return this.fillFields(ticketGuide?.code, workflowType);
  }

  private getSelectedWorkflowType(ticketGuide?:ITicketGuideButton):string {
    const selectedButton = ticketGuide || this.selectedTicketGuide;
    if (selectedButton) {
      return selectedButton.workflowType;
    }
    if (this.workflowTypes.length === 1) {
      return this.workflowTypes[0];
    }
  }

  private getWritePermission(): boolean {
    return this.selectedTicketGuide?.code === SOW_TYPE_SERVICE? this.sowInfo?.hasSow : true;
  }

  private checkOpenOrder() {
    if (this.userService.isCustomerCompany) {

      if (!this.searchResultTemplate.templateOptions.inputs.orders?.length) {
        this.setOrderSearchBusy(true);

        this.searchDataService.myOpenOrders()
          .then(this.handleOrderSearchResults)
          .finally(() => {
            this.setOrderSearchBusy(false);
          });
      }
    }
  }

  private fillFields(ticketCode:string, workflowType:string):Promise<any> {
    const promises:Promise<any>[] = [];

    promises.push(this.formlyService.fillField(this.categoryField, this.ticketDataService.getCategories(ticketCode, workflowType)));

    if (workflowType === TICKET_WORKFLOW_TYPE_WEBSHOP) {
      //pre-fill user's home country
      if (this.userService.isCustomerCompany || this.userService.isPartnerCompany) {
        this.model.country = this.currentUser.location.companyCountry.country;
      }

      if (!this.webshopField.templateOptions?.options?.length) {
        promises.push(
          this.formlyService.fillField(this.webshopField, this.webshopDataService.webshopsWithReadOnlyAccess())
          .then((webshops:INamedObject[]) => {
            if (webshops.length === 1) {
              this.webshopField.formControl.setValue(webshops[0]);
            }
            // preselected webshop id if provided
            if (this.webshopId) {
              this.webshopField.formControl.setValue(_.findWhere(webshops, { id: Number(this.webshopId) || '' }));
            }
          }));
      }

      if (!this.countryField.templateOptions?.options?.length) {
        promises.push(
          this.formlyService.fillField(this.countryField, this.lovService.list(LOV_COUNTRY))
            .then((countries:ICountry[]) => {
              // preselected webshop country id if provided
              if (this.countryId) {
                this.countryField.formControl.setValue(_.findWhere(countries, { id: Number(this.countryId) || '' }));
              }
            })
        );
      }
    }

    return Promise.all(promises);
  }

  private updateFaqs():void {
    if (this.faqField) {
      this.faqField.hide = true;
    }

    const workflowType = this.getSelectedWorkflowType();
    if (this.selectedTicketGuide && this.model.category && !this.isTrainingTicket) {
      this.ticketDataService.getTicketFaqs(workflowType, this.model.category.categoryType)
        .then(faqs => {
          this.faqField.templateOptions.inputs.faqs = faqs;
          this.faqField.hide = !faqs.length;
        });
    } else {
      if (this.faqField) {
        this.faqField.templateOptions.inputs.faqs = [];
      }
    }
  }

  private onCategoryChange = (category:ITicketCategory) => {
    // update faqs
    this.updateFaqs();
    if (!this.comments) {
      if (this.isTrainingTicket) {
        this.model.comments = category?.comments;
      } else {
        this.form.patchValue({ comments: category?.comments || '' });
      }
    }

    if (this.getSelectedWorkflowType() === TICKET_WORKFLOW_TYPE_TRAINING) {
      // when workflow type is training then set ticket title to selected category
      this.model.description = this.model.category?.description ? `ACSL Training - ${this.model.category?.description}` : '';
    }
  };

  private createFields():IExtraFieldConfigurationObject[] {
    const formLabel = {
      category: 'APP.TICKET.FORMLABEL.CATEGORY',
      order: 'APP.TICKET.FORMLABEL.ORDER',
      description: 'APP.TICKET.FORMLABEL.DESCRIPTION',
      comments: 'APP.TICKET.FORMLABEL.COMMENTS',
      webshop: 'APP.TICKET.FORMLABEL.WEBSHOP',
      country: 'APP.TICKET.FORMLABEL.COUNTRY',
      faq: 'APP.TICKET.FORMLABEL.FAQ'
    };

    const labelClass = 'col-sm-3';
    const valueClass = 'col-sm-9';
    const singleRowClass = 'col-sm-12';

    this.categoryField = this.formlyService.aSelect('category', formLabel.category)
      .withFormat(this.formattingService.formatTicketCategory)
      .isRequired()
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withExtraOptions({
        templateOptions: {
          trackBy: 'categoryType'
        }
      })
      .onChange(this.onCategoryChange)
      .build();

    this.faqField = this.formlyService.aCustom()
      .withComponent(FaqTicketCategoryComponent)
      .isHide()
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withExtraOptions({
        templateOptions: {
          inputs: {
            label: formLabel.faq,
            faqs: []
          }
        }
      })
      .build();

    this.webshopField = this.formlyService.aSelect('webshop', formLabel.webshop)
      .isRequired()
      .withFormat(this.formattingService.formatCompany)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .build();

    this.countryField = this.formlyService.aSelect('country', formLabel.country)
      .isRequired()
      .withFormat(this.formattingService.formatCountry)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .build();

    this.orderField = this.formlyService.anInput('orderKeywords', formLabel.order)
      .withPlaceholder('APP.TICKET.FORMPLACEHOLDER.ORDER')
      .isLoading()
      .withLoading(false)
      .onEnter(this.searchOrder)
      .withAddOnButton({
        component: OrderKeywordsAddonComponent,
        inputs: {
          busy: false,
          orderKeywords: () => this.model?.orderKeywords
        },
        outputs: {
          onSearchOrder: this.searchOrder
        }
      })
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .build();

    this.searchResultTemplate = this.formlyService.aCustom()
      .withComponent(OrderSearchResultComponent)
      .withExtraOptions({
        templateOptions: {
          inputs: {
            checkSOWBusy: this.checkSOWBusy,
            orders: [],
            model: this.model
          },
          outputs: {
            onOrderIdChange: this.onOrderSelectionChange
          }
        }
      })
      .isHide()
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .build();

    this.sowActivationField = this.formlyService.aCustom()
      .withComponent(SowActivationComponent)
      .isHide()
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withExtraOptions({
        templateOptions: {
          inputs: {
            ticketId: ''
          }
        }
      })
      .build();

    // main form fields required #1
    let formFields = [
      this.categoryField,
      this.faqField
    ];

    // TODO_PO workflow type form fields
    const orderTicketFields = [
      this.orderField,
      this.searchResultTemplate,
      this.sowActivationField
    ];

    // TODO_PR workflow type form fields
    const webshopTicketFields = [
      this.webshopField,
      this.countryField
    ];

    // concat the form to main form
    formFields = formFields.concat(orderTicketFields, webshopTicketFields);

    // main form fields required #2
    formFields.push(
      this.descriptionField = this.formlyService.anInput('description', formLabel.description)
        .isRequired()
        .withPlaceholder('APP.TICKET.FORMPLACEHOLDER.DESCRIPTION')
        .withLabelClass(singleRowClass)
        .withValueClass(singleRowClass)
        .isHide()
        .build(),

      this.commentsField = this.formlyService.aTextarea('comments', formLabel.comments)
        .isRequired()
        .withPlaceholder('APP.TICKET.FORMPLACEHOLDER.COMMENTS')
        .withLabelClass(singleRowClass)
        .withValueClass(singleRowClass)
        .isHide()
        .withExtraOptions({
          templateOptions: {
            editor: EDITOR_TINYMCE,
            editorCode: TINYMCE_ADVANCED
          },
          validators: {
            comments: this.formlyValidatorService.imageValidator
          }
        })
        .build()
    );

    return formFields;
  }

  private searchOrder = (orderId?:number):void => {
    const keyword = orderId? orderId.toString() : this.model.orderKeywords;

    if (keyword && keyword.length) {
      this.setOrderSearchBusy(true);

      const onSuccess = (results:ISearchResults[]) => {
        this.handleOrderSearchResults(results);
      };

      const onFail = util.noop;
      const onNotify = (notification:ISearchResults[]) => {
        this.handleOrderSearchResults(notification);
      };

      this.searchDataService.combinedSearch(keyword, onNotify)
        .then(onSuccess, onFail)
        .finally(() => {
          this.setOrderSearchBusy(false);
        });
    }
  };

  private handleOrderSearchResults = (results:ISearchResults[]) => {
    const orderResultSection = _.findWhere(results, { type: OrderSearchDataService.SEARCH_TYPE_ORDER });

    this.searchResultTemplate.hide = false;
    this.searchResultTemplate.templateOptions.inputs.orders = orderResultSection.data;

    if (!orderResultSection.data.length) {
      this.searchResultTemplate.hide = true;
    } else if (orderResultSection.data.length === 1) { // set default id if results only
      this.model.orderId = orderResultSection.data[0].id;
      this.searchResultTemplate.templateOptions.inputs.model = _.clone(this.model);
      this.onOrderSelectionChange(<IOrderSearchResult>orderResultSection.data[0]);
    }
  };

  private setOrderSearchBusy(busy:boolean):void {
    if (busy) {
      this.searchResultTemplate.hide = true;
    }
    this.orderField.templateOptions.addOnButton.inputs.busy = busy;
    this.orderField.templateOptions.disabled = busy;
  }

  private handleRedirectAfterAction(newTicket?:ITicket) {
    if (this.isInDialog) {
      this.onDialogClose.emit(newTicket);
    } else {
      if (this.previousState) {
        this.stateService.go(this.previousState, undefined, { inherit: false });
      } else {
        // go to homepage
        this.stateService.go(STATE_HOME_PAGE, undefined, { inherit: false });
      }
    }
  }

  public createTicket(): void {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        const workflowType = this.getSelectedWorkflowType();
        // order id is not checked using formly field
        if (workflowType === TICKET_WORKFLOW_TYPE_ORDER && !this.model.orderId) {
          const errors = [
            this.translationService.instant('GA.FORM.REQUIRED', { label: this.translationService.instant('APP.TICKET.FORMLABEL.ORDER') })
          ];
          this.messageService.showInvalidFormMessage(this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED'), this.translationService.instant('GA.VALIDATION.PLEASE_CORRECT'), errors, undefined, undefined);
          return;
        }

        // if original requester is defined then add to model
        if (this.requesterId) {
          this.model.originalRequester = this.requesterId;
        }

        this.addEditorDocumentIdsToModel();

        const postData = _.clone(this.model);
        delete postData.orderKeywords;
        if (workflowType === TICKET_WORKFLOW_TYPE_ORDER) {
          delete postData.webshop;
          delete postData.country;
        } else {
          delete postData.orderId;
        }

        this.saving = true;
        this.ticketDataService.submitTicket(postData)
          .then((newTicket) => {
            this.messageService.showFeedbackMessage(this.translationService.instant('APP.TICKET.MESSAGE.SUCCESS'));

            this.handleRedirectAfterAction(newTicket);
          })
          .finally(() => {
            this.saving = false;
          });
      })
      .catch();
  }

  public cancelForm():void {
    this.handleRedirectAfterAction();
  }

  private onOrderSelectionChange = (orderData: IOrderSearchResult): void => {
    // set order id
    this.model.orderId = Number(orderData?.id) || undefined;

    if (util.notNullOrUndefinedOrEmpty(orderData) && !this.checkSOWBusy && this.selectedTicketGuide?.code === SOW_TYPE_SERVICE) {
      this.setSOWBusy(true);
      // immediately hide title and body fields
      this.setTicketMessageHidden(true);
      this.sowActivationField.hide = true;

      this.sowDataService.checkSOWInfo(SOW_TYPE_SERVICE, orderData.webshopId, orderData.countryId)
        .then((res: ISOWInfo) => {
          this.sowInfo = res;
          this.hasWritePermission = this.getWritePermission();

          if (!this.sowInfo?.hasSow && this.sowInfo?.ticketId) {
            this.sowActivationField.hide = false;
            this.sowActivationField.templateOptions.inputs.ticketId = this.sowInfo.ticketId;
            // scroll to bottom
            this.scrollToBottom();
          } else if (!this.sowInfo?.hasSow && !this.sowInfo?.ticketId) {
            const buttons:IDialogButton[] = [
              {
                label: this.translationService.instant('APP.TICKET.BUTTONS.ACTIVATE_SOW_SERVICE'),
                className: 'btn-success'
              },
              {
                label: this.translationService.instant('GA.BUTTONS.CANCEL'),
                className: 'btn-danger'
              }
            ];

            const dialogCallback = (buttonName:string):void => {
              switch (buttonName) {
                case this.translationService.instant('APP.TICKET.BUTTONS.ACTIVATE_SOW_SERVICE'): {
                  this.createSOWActivationRequestTicket(orderData);
                  break;
                }

                case this.translationService.instant('GA.BUTTONS.CANCEL'): {
                  // clear 'sowInfo' object to make user able to retry this process
                  this.sowInfo = undefined;
                  break;
                }
              }
            };

            this.messageService.showMessageDialog(
              this.translationService.instant('APP.TICKET.BUTTONS.ACTIVATE_SOW_SERVICE'),
              this.translationService.instant('APP.TICKET.MESSAGE.SOW_SERVICE_REQUEST'),
              buttons,
              dialogCallback
            );
          }
        })
        .finally(() => {
          this.setSOWBusy(false);
          this.setTicketMessageHidden();
        });
    }
  };

  private setSOWBusy(isBusy: boolean): void {
    this.checkSOWBusy = isBusy;
    this.searchResultTemplate.templateOptions.inputs.checkSOWBusy = isBusy;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      this.scrollService.scrollToElement('.sow-service-result:eq(0)');
    });
  }

  private setTicketMessageHidden(isHidden?: boolean): void {
    // when workflow type is training then hide the subject field as it will use preset
    this.descriptionField.hide = this.isTrainingTicket || isHidden || !this.hasWritePermission;
    this.commentsField.hide = this.isTrainingTicket || isHidden || !this.hasWritePermission;
  }

  private createSOWActivationRequestTicket(orderData: IOrderSearchResult): ITicket {
    if (!orderData) { return; }

    const preparedTicketData: ITicket = {
      category: {
        workflowType: TICKET_WORKFLOW_TYPE_WEBSHOP,
        categoryType: 'TC'
      },
      webshop: {
        id: orderData.webshopId,
        name: orderData.webshop
      },
      country: {
        id: orderData.countryId,
        name: orderData.country
      },
      comments: '<p>Service tickets for this country are not enabled yet. Customer submitted a request to activate this feature.</p>',
      orderId: orderData.id,
      description: 'Service SOW request'
    };

    this.ticketDataService.submitTicket(preparedTicketData)
      .then((res: ITicket) => {
        this.messageService.showFeedbackAfterSave('APP.TICKET.MESSAGE.SOW_SERVICE_SUBMITTED');
        // reset form
        this.onTicketGuideChange(this.selectedTicketGuide);
        this.sowActivationField.templateOptions.inputs.ticketId = res.id;
        this.sowActivationField.hide = false;

        // scroll to bottom
        this.scrollToBottom();
      });
  }

  private addEditorDocumentIdsToModel():void{
    // add document ids to the model in order to make sure that the uploaded files are still present in the content of the editor
    const contentDocumentIds:number[] = this.editorConfigService.extractDocumentIdsFromContent(this.model.comments);
    if (contentDocumentIds.length) {
      this.model.documents = _.intersection(this.uploadedDocumentIds, contentDocumentIds);
    }
  }

  public ngOnDestroy(): void {
    // clear cache
    this.ticketDataService.clearCache();

    // destroy events
    this.stopTransitionEnterEventListener();
    this.uploadCompletedEventListener.unsubscribe();
  }
}